import {
	Box,
	Button,
	Card,
	Divider,
	Grid,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { OnoffType, QualityType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import AppImageView from '@/common/components/form-view/AppImageView';
import SingleInfoForm from './components/SingleInfoForm';
import { ExerciseTagDTO } from '@/api/DTO/common.interface';
import {
	useDeleteWishTreasureMutation,
	useGetWishTreasureDetailQuery,
	useSetWishTreasureStatusMutation,
} from '@/api/treasury/wishApiSlice';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function WishSingleDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteWishTreasure] = useDeleteWishTreasureMutation();
	const { wishId, wishSingleId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [setWishTreasureStatus] = useSetWishTreasureStatusMutation();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const { data: details } = useGetWishTreasureDetailQuery(wishSingleId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		// if (details?.data.status == OnoffType.on) {
		// 	return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
		// 		variant: 'error',
		// 		anchorOrigin: { horizontal: 'center', vertical: 'top' },
		// 		autoHideDuration: 3000,
		// 		action: (key) => {
		// 			return <CloseIcon onClick={() => closeSnackbar(key)} />;
		// 		},
		// 	});
		// }
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('wishList.deleteTreasure')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('wishList.isDeleteTreasure')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteWishTreasure(wishSingleId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteTreasureSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(`/treasury-manage/wish-list/info/${wishId}`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await setWishTreasureStatus({
			publishList: [
				{
					id: wishSingleId || '',
					published: type == 1 ? true : false,
				},
			],
			id: wishId || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.treasureDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							{/* <Grid item xs={6}>
								<AppFieldView
									label={t('wishList.layerLocation')}
									value={details?.data.createdAt}
								/>
							</Grid> */}
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('chartletList.treasureTitle')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('chartletList.quality')}
													value={showLabel(details?.data.quality, qualityList)}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('chartletList.integral')}
													value={details?.data.costPoints}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppImageView
													label={t('chartletList.treasureImage')}
													imageElement={
														details?.data.treasurePic && (
															<img
																src={details?.data.treasurePic.url}
																width={245}
																height={245}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('chartletList.treasureAllImage')}
													imageElement={
														details?.data.treasurePositionPic && (
															<img
																src={details?.data.treasurePositionPic.url}
																width={245}
																height={245}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<SingleInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
